// CRM Phase 3 — Post-sale Handoffs
import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Circle, PlayCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS = ["pending", "in_progress", "completed"];
const ICON = { pending: Circle, in_progress: PlayCircle, completed: CheckCircle2 };

export default function CrmHandoffs() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    const url = filter ? `/crm/handoffs?status=${filter}` : "/crm/handoffs";
    const { data } = await api.get(url);
    setRows(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const advance = async (h, status) => {
    try {
      await api.post(`/crm/handoffs/${h.id}/status`, { status });
      toast.success(`Moved to ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="crm-handoffs-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl">Post-sale Handoffs</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Won deals automatically become handoff records — support + logistics teams pick up from here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Filter:</span>
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="handoff-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {STATUS.map((s) => {
          const cnt = rows.filter((r) => r.status === s).length;
          return (
            <div key={s} className="bg-card border border-border rounded-2xl p-4">
              <div className="overline text-secondary text-xs capitalize">{s.replace("_", " ")}</div>
              <div className="font-display text-3xl mt-1">{cnt}</div>
            </div>
          );
        })}
      </div>

      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="text-sm text-muted-foreground bg-card border border-border rounded-2xl p-8 text-center">
            No handoffs yet. Won deals automatically create handoff records here.
          </div>
        )}
        {rows.map((h) => {
          const Icon = ICON[h.status] || Circle;
          return (
            <div key={h.id} data-testid={`handoff-row-${h.id}`} className="bg-card border border-border rounded-2xl p-5 flex items-start gap-4">
              <Icon className={`w-6 h-6 shrink-0 ${h.status === "completed" ? "text-primary" : h.status === "in_progress" ? "text-secondary" : "text-muted-foreground"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium">{h.contact_name || h.company || h.deal_id.slice(0, 8)}</div>
                  <span className="text-[10px] overline px-2 py-0.5 rounded-full bg-muted text-muted-foreground">£{(h.value_gbp || 0).toFixed(2)}</span>
                  <span className={`text-[10px] overline px-2 py-0.5 rounded-full capitalize ${
                    h.status === "completed" ? "bg-primary/15 text-primary" :
                    h.status === "in_progress" ? "bg-secondary/15 text-secondary" :
                    "bg-muted text-muted-foreground"
                  }`}>{h.status.replace("_", " ")}</span>
                </div>
                {h.company && <div className="text-xs text-muted-foreground mt-1">{h.company} · {h.contact_email || "—"}</div>}
                {h.kickoff_notes && <div className="text-sm text-foreground/80 mt-2">{h.kickoff_notes}</div>}
                <div className="text-xs text-muted-foreground mt-1">Tasks: {h.task_ids?.length || 0}</div>
              </div>
              <div className="flex items-center gap-1">
                {h.status === "pending" && (
                  <Button size="sm" onClick={() => advance(h, "in_progress")} data-testid={`handoff-start-${h.id}`}>Start <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
                )}
                {h.status === "in_progress" && (
                  <Button size="sm" onClick={() => advance(h, "completed")} data-testid={`handoff-complete-${h.id}`}>Complete <CheckCircle2 className="w-3.5 h-3.5 ml-1" /></Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
